import { Css, GridColumn, GridTable, column, emptyCell, simpleHeader } from "@homebound/beam";
import { Price, emptyCellDash, priceCell } from "src/components";
import {
  PurchaseOrderPage_CommitmentFragment,
  PurchaseOrderPage_LineItemsFragment,
  PurchaseOrderPage_TradeLineItemFragment,
} from "src/generated/graphql-types";
import { formatList } from "src/utils";
import { sanitizeHtml } from "src/utils/sanitizeHtml";

type CommitmentLineItemsTableProps = {
  commitment: PurchaseOrderPage_CommitmentFragment;
  enableProductConfigPlan: boolean;
};

export function CommitmentLineItemsTable({ commitment, enableProductConfigPlan }: CommitmentLineItemsTableProps) {
  const effectiveLineItems = getEffectiveLineItems(commitment.lineItems);
  const totalCostInCents = effectiveLineItems.sum((cli) => cli.costChangeInCents ?? 0);
  const totalSalesTaxInCents = effectiveLineItems.sum((cli) => cli.taxInCents);
  const totalTaxableCostInCents = effectiveLineItems.sum((cli) => cli.taxableCostInCents);
  const hasProratedLines =
    enableProductConfigPlan && effectiveLineItems.some((cli) => cli.primaryBidContractLineItem?.tradeLineItem);
  const showSkuColumn = effectiveLineItems.some((cli) => cli.projectItem.materialVariant?.modelNumber);
  const rows = hasProratedLines
    ? // Render every effective line item (primary + each modifying BCLI that carries its own trade line item)
      // as its own row. Drop expansions whose BCLI has no trade line item.
      createProductConfigRows(
        effectiveLineItems.filter(
          (cli) => !cli.primaryBidContractLineItem || cli.primaryBidContractLineItem.tradeLineItem,
        ),
      )
    : createPdfLineItemRows(effectiveLineItems, commitment.project.readyPlanConfig?.specOption?.readyPlanOption.id);

  return (
    <div css={Css.mt6.$}>
      <div css={Css.lgSb.mb4.$}>Line Items</div>
      <GridTable
        as="table"
        columns={createColumns(hasProratedLines, enableProductConfigPlan, showSkuColumn)}
        rows={[simpleHeader, ...rows]}
        style={{ bordered: false, allWhite: true }}
        rowStyles={{ header: { cellCss: Css.gray600.$ } }}
      />
      <div css={Css.df.fdc.p1.pr3.$}>
        {totalSalesTaxInCents !== 0 && (
          <>
            <div css={Css.f1.df.fwb.jcfe.$}>
              Subtotal:&nbsp;
              <Price id="subtotal" valueInCents={totalTaxableCostInCents} />
            </div>
            <div css={Css.f1.df.fwb.jcfe.$}>
              Sales Tax:&nbsp;
              <Price id="salesTax" valueInCents={totalSalesTaxInCents} />
            </div>
          </>
        )}
        <div css={Css.f1.df.fwb.jcfe.$}>
          Total:&nbsp;
          <Price id="totalCost" valueInCents={totalCostInCents} />
        </div>
      </div>
    </div>
  );
}

type HeaderRow = { kind: "header" };
type OptionGroupRow = { kind: "group"; data: string };
type LineItemRow = { kind: "data"; data: PurchaseOrderPage_LineItemsFragment };
type SpecificationsRow = { kind: "specifications"; data: string };
type TradeLineItemRow = { kind: "tradeLineItem"; data: PurchaseOrderPage_TradeLineItemFragment[] | undefined };
type CommitmentRow = LineItemRow | HeaderRow | OptionGroupRow | SpecificationsRow | TradeLineItemRow;

function createColumns(
  hasProratedLines: boolean,
  enableProductConfigPlan: boolean,
  showSkuColumn: boolean,
): GridColumn<CommitmentRow>[] {
  return [
    ...(enableProductConfigPlan
      ? [
          column<CommitmentRow>({
            header: "Item",
            group: (groupName) =>
              !hasProratedLines ? { content: groupName, colspan: 9, css: Css.sm.px2.$ } : emptyCell,
            tradeLineItem: (data) =>
              hasProratedLines
                ? {
                    content: formatList(data?.flatMap((tli) => tli.options.map((o) => o.displayName)) ?? []) || "Base",
                    colspan: 5,
                    css: Css.sm.px2.$,
                  }
                : emptyCell,
            data: (cli) => cli.projectItem.item.name,
            specifications: emptyCell,
          }),
          column<CommitmentRow>({
            header: "Name",
            group: emptyCell,
            tradeLineItem: emptyCell,
            data: (cli) => cli.projectItem.name,
            specifications: (specification) => ({
              content: (
                <p data-testid="specification">
                  Note: <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(specification || "") }} />
                </p>
              ),
              colspan: 8,
              css: Css.sm.$,
            }),
          }),
          ...(showSkuColumn
            ? [
                column<CommitmentRow>({
                  header: "SKU",
                  group: emptyCell,
                  tradeLineItem: emptyCell,
                  data: (cli) => cli.projectItem.materialVariant?.modelNumber || "-",
                  specifications: emptyCell,
                }),
              ]
            : []),
          column<CommitmentRow>({
            header: "Location",
            group: emptyCell,
            tradeLineItem: emptyCell,
            data: (cli) => cli.projectItem.itemTemplateItem?.location.displayLocationPath || "-",
            specifications: emptyCell,
          }),
        ]
      : [
          column<CommitmentRow>({
            header: "Item Code",
            group: (groupName) => ({ content: groupName, colspan: 9, css: Css.sm.px2.$ }),
            tradeLineItem: () => emptyCell,
            data: (cli) => ({ content: cli.projectItem.item.fullCode, typeScale: "xs" }),
            specifications: emptyCell,
          }),
          column<CommitmentRow>({
            header: "Item Description",
            group: emptyCell,
            tradeLineItem: emptyCell,
            data: (cli) => cli.projectItem.name,
            specifications: (specification) => ({
              content: (
                <p data-testid="specification">
                  Note: <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(specification || "") }} />
                </p>
              ),
              colspan: 8,
              css: Css.sm.$,
            }),
          }),
          column<CommitmentRow>({
            header: "Bid Item",
            group: emptyCell,
            tradeLineItem: emptyCell,
            data: ({ bidItem }) => (bidItem ? bidItem.displayName : emptyCellDash),
            specifications: emptyCell,
          }),
          column<CommitmentRow>({
            header: "Location",
            group: emptyCell,
            tradeLineItem: emptyCell,
            data: (cli) => cli.projectItem.location?.name || "-",
            specifications: emptyCell,
          }),
        ]),
    column<CommitmentRow>({
      header: "Cost Type",
      group: emptyCell,
      tradeLineItem: emptyCell,
      data: (cli) => cli.projectItem.costTypeDetail.tradePartnerName,
      specifications: emptyCell,
    }),
    column<CommitmentRow>({
      header: "Qty",
      group: emptyCell,
      tradeLineItem: emptyCell,
      data: (cli) => (cli.projectItem.unitOfMeasure.useQuantity ? cli.quantity : ""),
      specifications: emptyCell,
    }),
    column<CommitmentRow>({
      header: "Units",
      group: emptyCell,
      tradeLineItem: emptyCell,
      data: (cli) => cli.projectItem.unitOfMeasure.abbreviation,
      specifications: emptyCell,
    }),
    column<CommitmentRow>({
      header: "Unit Cost",
      group: emptyCell,
      tradeLineItem: emptyCell,
      data: (cli) => {
        if (cli.projectItem.unitOfMeasure.useQuantity && !cli.primaryBidContractLineItem?.tradeLineItem) {
          const { quantity, taxableCostInCents } = cli;
          return priceCell({ valueInCents: (taxableCostInCents || 0) / (quantity || 1) });
        } else {
          return emptyCellDash;
        }
      },
      specifications: emptyCell,
    }),
    column<CommitmentRow>({
      header: "Cost",
      tradeLineItem: (data) => (data?.length === 1 ? priceCell({ valueInCents: data[0].totalCostInCents }) : emptyCell),
      group: emptyCell,
      data: (cli) =>
        cli.primaryBidContractLineItem?.tradeLineItem ? emptyCell : priceCell({ valueInCents: cli.taxableCostInCents }),
      specifications: emptyCell,
    }),
  ];
}

function createProductConfigRows(lineItems: PurchaseOrderPage_LineItemsFragment[]) {
  const lineItemsByTrade = lineItems.groupBy((cli) => cli.primaryBidContractLineItem?.tradeLineItem?.id ?? "");
  return Object.entries(lineItemsByTrade)
    .sortBy(([, group]) => {
      // Render base items first
      const tli = group.first?.primaryBidContractLineItem?.tradeLineItem;
      return tli?.options.isEmpty ? -Infinity : 0;
    })
    .map(([tliId, lineItems]) => ({
      kind: "tradeLineItem" as const,
      id: tliId,
      data: lineItems.first?.primaryBidContractLineItem?.tradeLineItem
        ? [lineItems.first?.primaryBidContractLineItem?.tradeLineItem]
        : undefined,
      children: lineItems.flatMap((cli) => [
        { id: cli.id, data: cli, kind: "data" as const },
        ...(cli.specifications
          ? [{ id: `${cli.id}-specifications`, data: cli.specifications, kind: "specifications" as const }]
          : []),
      ]),
    }));
}

function getEffectiveLineItems(
  lineItems: PurchaseOrderPage_LineItemsFragment[],
): PurchaseOrderPage_LineItemsFragment[] {
  return lineItems
    .flatMap((cli) =>
      cli.modifyingBidContractLineItems.isEmpty
        ? cli
        : [cli.primaryBidContractLineItem, ...cli.modifyingBidContractLineItems].compact().map((bcli) => ({
            ...cli,
            id: bcli.id,
            primaryBidContractLineItem: bcli,
            costChangeInCents: bcli?.totalCostInCents,
            taxableCostInCents: bcli?.totalCostInCents,
            taxInCents: 0,
          })),
    )
    .sortBy((cli) => [cli.projectItem.item.fullCode, cli.projectItem.name]);
}

function filterLineItemOptions(
  lineItem: PurchaseOrderPage_LineItemsFragment,
  specOptionId: string | undefined,
): PurchaseOrderPage_LineItemsFragment {
  const lineOptions =
    (lineItem.bidItem
      ? lineItem.projectItem.itemTemplateItem?.options
      : lineItem.primaryBidContractLineItem?.itemTemplateItem?.options) ?? [];
  return {
    ...lineItem,
    projectItem: {
      ...lineItem.projectItem,
      configuredOptions: lineOptions.filter(
        (option) => option.id !== specOptionId && !option.optionGroup.group.type.isElevation,
      ),
    },
  };
}

function getConfiguredOptionsKey(cli: PurchaseOrderPage_LineItemsFragment) {
  return (
    cli.projectItem.configuredOptions
      .sortBy((o) => o.order)
      .map((o) => o.id)
      .join("-") ?? ""
  );
}

function getGroupName(lineItems: PurchaseOrderPage_LineItemsFragment[]) {
  const options = lineItems.first?.projectItem.configuredOptions.map((option) => option.displayName) ?? [];
  return options.nonEmpty ? options.join(", ") : "Base";
}

function createPdfLineItemRows(lineItems: PurchaseOrderPage_LineItemsFragment[], specOptionId: string | undefined) {
  return lineItems
    .map((li) => filterLineItemOptions(li, specOptionId))
    .groupByObject((cli) => getConfiguredOptionsKey(cli))
    .sortBy(([, lineItems]) => lineItems?.first?.projectItem.configuredOptions.sum((o) => o.optionGroup.order) ?? 0)
    .map(([key, lineItems]) => ({
      id: key,
      kind: "group" as const,
      data: getGroupName(lineItems),
      children: lineItems.flatMap((cli) => [
        { id: cli.id, data: cli, kind: "data" as const },
        ...(cli.specifications
          ? [{ id: `${cli.id}-specifications`, data: cli.specifications, kind: "specifications" as const }]
          : []),
      ]),
    }));
}
